import { Header } from '@components/Header'
import { LanguageSelectionStep } from '@components/LanguageSelectionStep'
import * as Sentry from '@sentry/browser'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'

import { Loading } from '@/components/Loading'
import { Module } from '@/components/Module'
import { useConfig } from '@/hooks/useConfig'
import { useJourney } from '@/hooks/useJourney'
import { useLoading } from '@/hooks/useLoading'
import { useModule } from '@/hooks/useModule'
import { useStep } from '@/hooks/useStep'
import { useUser } from '@/hooks/useUser'
import i18n from '@/locale/i18n'
import { MODULE_TYPE } from '@/types/module'
import { hasServiceSelector } from '@/utils/hasServiceSelector'

function App() {
  const { pathname } = useLocation()
  const { t } = useTranslation()

  const { initialized } = useJourney()
  const { config } = useConfig()
  const { user } = useUser()
  const { loading } = useLoading()
  const { currentModule } = useModule()
  const { currentStep } = useStep()
  const [isLanguageSelected, setIsLanguageSelected] = useState<boolean | null>(null)

  const footerKey = `footer.project_number.${pathname.split('/')[1]}`
  const footerTranslation = t(footerKey)
  const footerHasTranslation = footerTranslation !== footerKey

  useEffect(() => {
    Sentry.setContext('language', { language: i18n.language })
  }, [i18n.language])

  useEffect(() => {
    if (!config) return

    if (!config.languageScreenMandatory) return setIsLanguageSelected(true)

    const lsLanguageSelected = localStorage.getItem('languageSelected')
    if (lsLanguageSelected) i18n.changeLanguage(lsLanguageSelected) // Set current language to the one stored in localStorage
    setIsLanguageSelected(Boolean(lsLanguageSelected))
  }, [config])

  const hasHeader = (!hasServiceSelector() && currentModule?.type === MODULE_TYPE.CONSULTATION) || currentModule?.type !== MODULE_TYPE.CONSULTATION

  return (
    <div className="flex w-full flex-1 flex-col items-center justify-center sm:p-4">
      <div className="relative flex h-full w-full max-w-[640px] grow flex-col overflow-clip sm:rounded-lg sm:shadow-strong">
        {hasHeader && <Header />}

        <div className="relative flex grow flex-col overflow-y-auto">
          {initialized && <Module />}
          {!isLanguageSelected && config?.languageScreenMandatory && <LanguageSelectionStep />}
        </div>

        {footerHasTranslation && <div className="bg-primary p-2 text-center text-xs text-white">{footerTranslation}</div>}

        {!import.meta.env.PROD && (
          <div className="flex flex-col p-2">
            <span className="text-center text-xs">
              <b>User conditions</b> ·{' '}
              {(user?.routeConditions &&
                Object.entries(user?.routeConditions)
                  .map(([key, value]) => `${key}: ${value}`)
                  .join(', ')) ||
                'none'}
            </span>
            <span className="text-center text-xs">
              <b>Current Module</b> · #{currentModule?.id} ({currentModule?.name})
            </span>
            <span className="text-center text-xs">
              <b>Current Step</b> · #{currentStep?.id} ({currentStep?.name})
            </span>
          </div>
        )}

        {loading && <Loading />}
      </div>
    </div>
  )
}

export default App
